import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { Button, Card, Checkbox, Flex, Skeleton, Tooltip, Upload, message } from 'antd';
import { useTranslation } from 'react-i18next';

// components
import Icon from 'utils/icon';
import ComponentHeader from 'components/componentHeader/componentHeader';
import PageTitle from 'components/title/title';
import TreasurySharesComponent, { TreasurySharesComponentRef } from '../components/treasurySharesComponent';
import ShareholderListComponent, { ShareholderListComponentRef } from '../components/shareholdersListComponent';
import { onDownloadTemplate, onGetRemainingShares, onSendForReview, onUploadTemplate } from '../services/shareholdersService';
import NotificationService from 'core/services/notification';
import { formatErrorMessages } from 'utils/errorMessage';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { updateCompany } from 'stores/useCompanyStore';
import NumberElement from 'components/number/number';
import { RootState } from 'stores/store';
import { downloadFile } from 'utils/downloadFile';
import { RcFile } from 'antd/es/upload';
import useCooperative from 'core/hooks/useCooperative';

const ShareholdersRegistryPage: FunctionComponent = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const treasuryRef = useRef<TreasurySharesComponentRef>(null);
	const shareholdersRef = useRef<ShareholderListComponentRef>(null);

	// global states
	const company = useSelector((state: RootState) => state.company);
	const { isCooperative } = useCooperative();

	// local states
	const [editable, setEditable] = useState(false);
	const [remainingSharesLoading, setRemainingSharesLoading] = useState(false);
	const [remainingShares, setRemainingShares] = useState<number>(0);
	const [acknowledge, setAcknowledge] = useState(false);

	const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
	const [isUploadingTemplate, setIsUploadingTemplate] = useState(false);
	const [isSendingForReview, setIsSendingForReview] = useState(false);

	useEffect(() => {
		getRemainingShares();

		if (['Waiting', 'SavedForLater', 'Rejected'].includes(company?.company_shareholder_status!)) {
			setEditable(true);
		}
	}, []);

	const getRemainingShares = async () => {
		try {
			setRemainingSharesLoading(true);
			const response = await onGetRemainingShares();
			setRemainingShares(response.remaining_shares_to_add);
			setRemainingSharesLoading(false);
		} catch (error: any) {
			setRemainingSharesLoading(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		}
	};

	const downloadFileHandler = async () => {
		try {
			setIsDownloadingTemplate(true);
			const response = await onDownloadTemplate();

			downloadFile(response.download_url, `shareholders_template.xlsx`);

			setIsDownloadingTemplate(false);
		} catch (error) {
			setIsDownloadingTemplate(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		}
	};

	const uploadFileHandler = async (file: RcFile) => {
		try {
			const isXlsx = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || file.name.endsWith('.xlsx');

			if (!isXlsx) {
				message.error(t('COMMON.ONLY_XLSX'));
			}

			setIsUploadingTemplate(true);

			const payload = new FormData();
			payload.append('file', file);

			await onUploadTemplate(payload);

			getRemainingShares();
			treasuryRef.current?.refreshTreasuryShares();
			shareholdersRef.current?.refreshShareholders();

			setIsUploadingTemplate(false);
			return false;
		} catch (error: any) {
			setIsUploadingTemplate(false);

			if (error.status === 422) {
				NotificationService.error(t('COMMON.ERROR'), formatErrorMessages(error));
				return false;
			}

			NotificationService.error(t('COMMON.ERROR'), t('COMMON.SUBMIT_ERROR'));
			return false;
		}
	};

	const sendForReviewHandler = async () => {
		try {
			if (!acknowledge) return;

			setIsSendingForReview(true);
			await onSendForReview();

			dispatch(updateCompany({ company_shareholder_status: 'UnderReview' }));

			setIsSendingForReview(false);
			NotificationService.success(t('COMMON.SUCCESS'), t('SHAREHOLDERS_REGISTRY.SENT_FOR_REVIEW_SUCCESS'));
			navigate('/dashboard');
		} catch (error: any) {
			setIsSendingForReview(false);
			if (error.status === 422) NotificationService.error(t('COMMON.ERROR'), formatErrorMessages(error));
			else NotificationService.error(t('COMMON.ERROR'), t('COMMON.SUBMIT_ERROR'));
		}
	};

	return (
		<>
			<PageTitle
				name={
					company?.company_shareholder_status! == 'Waiting'
						? t('SIDEBAR.ADD_SHAREHOLDERS_REGISTRY')
						: company?.company_shareholder_status! == 'Rejected'
							? t('SIDEBAR.UPDATE_SHAREHOLDERS_REGISTRY')
							: t('SIDEBAR.VIEW_SHAREHOLDERS_REGISTRY')
				}
			/>

			<Card className="mt-4">
				<ComponentHeader
					title={editable ? t('SHAREHOLDERS_REGISTRY.ADD') : t('SHAREHOLDERS_REGISTRY.ADDED')}
					subtitle={editable ? t('SHAREHOLDERS_REGISTRY.ADD_DESC') : ''}
					style={{ marginBottom: 40 }}
				/>

				{editable && (
					<>
						<Flex gap={20}>
							<Button size="middle" onClick={downloadFileHandler} loading={isDownloadingTemplate} disabled={isDownloadingTemplate}>
								<Icon name="download" color="#344054" />
								{t('SHAREHOLDERS_REGISTRY.DOWNLOAD_TEMPLATE')}
							</Button>

							<Upload name="file" beforeUpload={uploadFileHandler} showUploadList={false} accept=".xlsx" className="uploadBtn">
								<Button size="middle" type="primary" loading={isUploadingTemplate} disabled={isUploadingTemplate}>
									{t('SHAREHOLDERS_REGISTRY.UPLOAD_DATA')}
								</Button>
							</Upload>
						</Flex>
					</>
				)}

				{!isCooperative && <TreasurySharesComponent onChange={getRemainingShares} editable={editable} ref={treasuryRef} />}
			</Card>

			<Card className="my-5">
				<ShareholderListComponent onChange={getRemainingShares} remainingShares={remainingShares} editable={editable} ref={shareholdersRef} />

				{editable && (
					<>
						{remainingSharesLoading ? (
							<Skeleton.Button active block style={{ height: 73, marginTop: 30, borderRadius: 'var(--radius)' }} />
						) : (
							<Card
								style={{
									backgroundColor: remainingShares ? '#ffe0e0' : '#b8ffdb',
									fontSize: 16,
									color: remainingShares ? '#e45757' : '#50a275',
									marginTop: 30,
									display: 'flex',
									justifyContent: 'flex-end',
								}}>
								{remainingShares != 0 ? (
									<Flex align="center" gap={5}>
										{t('SHAREHOLDERS_REGISTRY.REMAINING')}
										<NumberElement value={remainingShares} style={{ fontWeight: 600 }} />
										{t('SHAREHOLDERS_REGISTRY.FROM')}
										<NumberElement value={company?.number_of_shares} style={{ fontWeight: 600 }} />
										{t('COMMON.SHARES')}
									</Flex>
								) : (
									<p className="mb-0 fw-medium">
										{t('SHAREHOLDERS_REGISTRY.ALL_SHARES_ALLOCATED', { num: company?.number_of_shares.toLocaleString() })}
									</p>
								)}
							</Card>
						)}

						<div>
							<Flex justify="space-between" align="center" gap={20} className="mt-5">
								<Checkbox onChange={(event) => setAcknowledge(event.target.checked)} style={{ fontSize: 15 }}>
									{t('COMMON.ACKNOWLEDGE')}
								</Checkbox>
								<Tooltip
									color="red"
									title={remainingShares > 0 ? t('SHAREHOLDERS_REGISTRY.MORE_SHARES_AVAILABLE', { shares: remainingShares.toLocaleString() }) : ''}>
									<Button type="primary" loading={isSendingForReview} onClick={sendForReviewHandler} disabled={!acknowledge || remainingShares! > 0}>
										{t('SHAREHOLDERS_REGISTRY.SEND_FOR_REVIEW')}
									</Button>
								</Tooltip>
							</Flex>
						</div>
					</>
				)}
			</Card>
		</>
	);
};

export default ShareholdersRegistryPage;
